import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { getActive } from '../../services/activeService';
import { getDriver } from '../../services/driverService';
import { getVehicle } from '../../services/vehicleService';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { timeZone: 'UTC' });
  } catch (e) {
    return '—';
  }
};

const expiryBadge = (value) => {
  if (!value) return <span className="badge badge-warning">Missing</span>;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return <span className="badge badge-warning">Invalid</span>;
  const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
  if (diff < 0) return <span className="badge badge-warning">Expired</span>;
  if (diff < 30) return <span className="badge badge-warning">Expiring soon</span>;
  return <span className="badge badge-success">Compliant</span>;
};

const pickFirst = (...values) => values.find((v) => v !== undefined && v !== null && v !== '');

const minutesToHours = (minutes) =>
  `${Math.round(((minutes || 0) / 60) * 10) / 10} hrs`;

const ActivesView = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fullDriver, setFullDriver] = useState(null);
  const [fullVehicle, setFullVehicle] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getActive(id);
        const payload = res.data?.active || res.data || null;
        setRecord(payload);

        try {
          const driverId = payload?.driverId || payload?.driver?._id || payload?.driver?.id;
          if (driverId) {
            const dr = await getDriver(driverId);
            const full = dr.data?.driver || dr.data || null;
            if (full) setFullDriver(full);
          }
        } catch {}

        try {
          const vehicleId =
            payload?.vehicle?._id ||
            payload?.vehicle?.id ||
            payload?.cab?._id ||
            payload?.cab?.id ||
            payload?.vehicleId;
          if (vehicleId) {
            const vr = await getVehicle(vehicleId);
            const fullV = vr.data?.vehicle || vr.data || null;
            if (fullV) setFullVehicle(fullV);
          } else {
            const cab =
              payload?.cabNumber ||
              payload?.cab?.cabNumber ||
              payload?.vehicle?.cabNumber;
            if (cab) {
              const byCab = await import('../../services/vehicleService').then((m) =>
                m.getVehicleByCabNumber(cab),
              );
              const fullV = byCab?.vehicle || null;
              if (fullV) setFullVehicle(fullV);
            }
          }
        } catch {}
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load active record');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const normalize = (rec) => {
    if (!rec) return null;
    const driver = fullDriver || rec.driver || rec.driverInfo || rec.driverProfile || {};
    const vehicle = fullVehicle || rec.vehicle || rec.vehicleInfo || rec.cab || {};
    const rawSnapshot = pickFirst(rec.vehicleCompliance, vehicle.vehicleCompliance);

    return {
      _id: rec._id,
      driverId: pickFirst(rec.driverId, driver._id, driver.id, driver.driverId),
      firstName: pickFirst(rec.firstName, driver.firstName, driver.givenName),
      lastName: pickFirst(rec.lastName, driver.lastName, driver.familyName),
      phone: pickFirst(driver.phoneNumber, driver.phone),
      email: pickFirst(driver.email),
      cabNumber: pickFirst(rec.cabNumber, vehicle.cabNumber),
      licPlates: pickFirst(rec.licPlates, vehicle.licPlates, vehicle.licensePlate),
      make: pickFirst(rec.make, vehicle.make),
      model: pickFirst(rec.model, vehicle.model),
      color: pickFirst(rec.color, vehicle.color),
      status: pickFirst(rec.status, rec.currentStatus, 'Inactive'),
      availability: pickFirst(rec.availability, rec.currentAvailability, 'Offline'),
      dlExpiry: pickFirst(driver.dlExpiry, driver.licenseExpiry),
      cbiExpiry: pickFirst(driver.cbiExpiry),
      dotExpiry: pickFirst(driver.dotExpiry),
      regisExpiry: pickFirst(rec.regisExpiry, vehicle.regisExpiry, vehicle.registrationExpiry),
      annualInspection: pickFirst(rec.annualInspection, vehicle.annualInspection),
      vehicleCompliance: rawSnapshot || null,
      lastPing: rec.currentLocation?.updatedAt || rec.updatedAt || rec.createdAt,
      hoursOfService: rec.hoursOfService || null,
    };
  };

  const issueLabel = {
    registrationExpired: 'Registration expired',
    inspectionExpired: 'Annual inspection expired',
    registrationMissing: 'Registration missing',
    inspectionMissing: 'Annual inspection missing',
  };

  const renderBody = () => {
    if (loading) return <div className="skeleton" style={{ height: '240px' }} />;
    if (error) return <div className="feedback error">{error}</div>;
    if (!record) return <div className="empty-state">Active record not found.</div>;

    const r = normalize(record);
    const hos = r.hoursOfService;
    const isOnline = String(r.availability || '').toLowerCase() === 'online';
    const isActive = String(r.status || '').toLowerCase() === 'active';
    const vehicleIssues = r.vehicleCompliance?.issues || [];

    return (
      <div className="panel">
        <div className="panel-header">
          <div>
            <h2>
              {r.firstName || 'Driver'} {r.lastName || ''}
            </h2>
            <p className="panel-subtitle" style={{ marginTop: 4 }}>
              Driver ID: {r.driverId || '—'}
            </p>
          </div>
          <div className="panel-actions">
            <span className={`badge ${isActive ? 'badge-success' : 'badge-warning'}`}>
              {r.status}
            </span>
            <span className={`badge ${isOnline ? 'badge-success' : 'badge-info'}`}>
              {r.availability}
            </span>
            <Link to={`/actives/${r._id}`} className="btn btn-primary">
              Manage
            </Link>
            <Link to="/actives" className="btn btn-subtle">
              Back to roster
            </Link>
          </div>
        </div>

        <div className="panel-body">
          <div className="grid-two">
            <div className="metric-card">
              <h3>Driver</h3>
              <div className="view-stat-row">
                <div className="view-stat">
                  <span className="view-stat-label">Phone</span>
                  <span className="view-stat-value">{r.phone || '—'}</span>
                </div>
                <div className="view-stat">
                  <span className="view-stat-label">Email</span>
                  <span className="view-stat-value">{r.email || '—'}</span>
                </div>
                <div className="view-stat">
                  <span className="view-stat-label">Last ping</span>
                  <span className="view-stat-value">
                    {r.lastPing ? new Date(r.lastPing).toLocaleString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            <div className="metric-card">
              <h3>Vehicle</h3>
              <p className="metric-subline">Cab #{r.cabNumber || '—'}</p>
              <div className="view-stat-row">
                <div className="view-stat">
                  <span className="view-stat-label">Plates</span>
                  <span className="view-stat-value">{r.licPlates || '—'}</span>
                </div>
                <div className="view-stat">
                  <span className="view-stat-label">Make & model</span>
                  <span className="view-stat-value">
                    {[r.make, r.model].filter(Boolean).join(' ') || '—'}
                  </span>
                </div>
                <div className="view-stat">
                  <span className="view-stat-label">Color</span>
                  <span className="view-stat-value">{r.color || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid-two">
            <div className="metric-card">
              <h3>Driver compliance</h3>
              <p className="metric-subline">Key expiry dates</p>
              <div className="compliance-grid">
                {[
                  { label: 'DL expiry', value: r.dlExpiry },
                  { label: 'CBI', value: r.cbiExpiry },
                  { label: 'DOT', value: r.dotExpiry },
                ].map(({ label, value }) => (
                  <div className="compliance-item" key={label}>
                    <span className="view-stat-label">{label}</span>
                    <span className="view-stat-value">{formatDate(value)}</span>
                    {expiryBadge(value)}
                  </div>
                ))}
              </div>
            </div>

            <div className="metric-card">
              <h3>Vehicle compliance</h3>
              <p className="metric-subline">Registration & inspections</p>
              <div className="compliance-grid">
                {r.vehicleCompliance && (
                  <div className="compliance-item">
                    <span className="view-stat-label">Overall</span>
                    <span className="view-stat-value">
                      {r.vehicleCompliance.isCompliant ? 'Compliant' : 'Non-compliant'}
                    </span>
                    {r.vehicleCompliance.isCompliant ? (
                      <span className="badge badge-success">Compliant</span>
                    ) : (
                      <span className="badge badge-warning">Non-compliant</span>
                    )}
                  </div>
                )}
                <div className="compliance-item">
                  <span className="view-stat-label">Registration</span>
                  <span className="view-stat-value">{formatDate(r.regisExpiry)}</span>
                  {expiryBadge(r.regisExpiry)}
                </div>
                <div className="compliance-item">
                  <span className="view-stat-label">Annual inspection</span>
                  <span className="view-stat-value">{formatDate(r.annualInspection)}</span>
                  {expiryBadge(r.annualInspection)}
                </div>
              </div>
              {vehicleIssues.length > 0 && (
                <div className="compliance-issues">
                  <span className="view-stat-label">Issues</span>
                  <ul>
                    {vehicleIssues.map((it, i) => (
                      <li key={i}>{issueLabel[it] || String(it)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {hos && (
            <div className="metric-card">
              <h3>Hours of service</h3>
              <div className="view-stat-row" style={{ marginTop: 12 }}>
                <div className="view-stat">
                  <span className="view-stat-label">On duty</span>
                  <span className="view-stat-value">
                    {minutesToHours(hos.cumulativeOnDutyMinutes || hos.totalHours * 60)}
                  </span>
                </div>
                <div className="view-stat">
                  <span className="view-stat-label">Driving time</span>
                  <span className="view-stat-value">
                    {minutesToHours(hos.cumulativeDrivingMinutes || hos.drivingHours * 60)}
                  </span>
                </div>
                {hos.breakMinutes > 0 && (
                  <div className="view-stat">
                    <span className="view-stat-label">Break</span>
                    <span className="view-stat-value">{hos.breakMinutes} min</span>
                  </div>
                )}
                {Array.isArray(hos.violations) && hos.violations.length > 0 && (
                  <div className="view-stat">
                    <span className="view-stat-label">Violations</span>
                    <span className="view-stat-value" style={{ color: 'var(--danger)' }}>
                      {hos.violations.length}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <AppLayout
      title="Active record"
      subtitle="Read-only view of the active roster entry and expiry highlights"
    >
      <div className="surface">{renderBody()}</div>
    </AppLayout>
  );
};

export default ActivesView;
